import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { Box, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import * as UI from '@siloreal:shared:ui';

export const PLATFORM_GREEN = '#6baa00';
export const FALLBACK_STRING = '—';

const PRIMARY_TEXT_SX = {
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.25,
  letterSpacing: '0.01em',
} as const;

export function FieldCellStack({ children }: { children: ReactNode }) {
  return (
    <Stack
      height="100%"
      width="100%"
      direction="row"
      alignItems="center"
      gap={0.75}
      sx={{ minWidth: 0, px: 1 }}
    >
      {children}
    </Stack>
  );
}

export function CalendarIconBadge({ active = true }: { active?: boolean }) {
  return (
    <Box
      aria-hidden
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
        borderRadius: '6px',
        flexShrink: 0,
        bgcolor: active ? 'rgba(107, 170, 0, 0.12)' : 'rgba(58, 52, 64, 0.06)',
        color: active ? PLATFORM_GREEN : '#b8b4ad',
      }}
    >
      <CalendarMonthOutlinedIcon sx={{ fontSize: 15 }} />
    </Box>
  );
}

export function FieldPrimaryText({
  children,
  title,
  disabled = false,
}: {
  children: ReactNode;
  title?: string;
  disabled?: boolean;
}) {
  return (
    <Typography
      noWrap
      title={title}
      sx={{
        ...PRIMARY_TEXT_SX,
        color: disabled ? '#b8b4ad' : '#3A3440',
        minWidth: 0,
      }}
    >
      {children}
    </Typography>
  );
}

export function MetaRow({
  tag,
  disabled = false,
  valueTitle,
  children,
}: {
  tag: string;
  disabled?: boolean;
  valueTitle?: string;
  children: ReactNode;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      aria-disabled={disabled}
      sx={{
        opacity: disabled ? 0.61 : 1,
        minWidth: 0,
        mt: 0.2,
        position: 'relative',
        borderRadius: '3px',
        overflow: 'hidden',
      }}
    >
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          px: 0.45,
          py: 0.1,
          borderRadius: '3px',
          fontSize: 8,
          fontWeight: 800,
          letterSpacing: '0.08em',
          lineHeight: 1.2,
          color: disabled ? '#b8b4ad' : '#6f6a62',
          backgroundColor: disabled ? '#f2f0ec' : '#ebe8e2',
        }}
      >
        {tag}
      </Box>

      <UI.Typography variant="secondary" strong={!disabled}>
        <Box
          component="span"
          title={valueTitle}
          sx={{
            display: 'block',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 100,
            fontSize: 12,
            lineHeight: 1.2,
            color: disabled ? '#b8b4ad' : undefined,
          }}
        >
          {children}
        </Box>
      </UI.Typography>
    </Stack>
  );
}
